import { type ComputedRef, computed } from 'vue'

import type { Bcp47Locale } from '@backmarket/http-api/src/standards/Locale'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useDataConsent } from '@backmarket/nuxt-module-tracking/useDataConsent'

import { useRouteLocationWithLocale } from '~/composables/useRouteLocationWithLocale'

import { footerConfigs } from '../config/footerConfigs'
import type {
  FooterColumn,
  FooterConsentLink,
  FooterExternalLink,
  FooterLink,
  FooterRouteLink,
  ResolvedFooterColumn,
  ResolvedFooterLink,
} from '../config/types'
import translations from '../translations'

function isRouteLink(link: FooterLink): link is FooterRouteLink {
  return 'route' in link
}

function isExternalLink(link: FooterLink): link is FooterExternalLink {
  return 'href' in link
}

function isConsentLink(link: FooterLink): link is FooterConsentLink {
  return 'action' in link
}

function isFeatureFlagActive(
  experiments: Record<string, string>,
  featureFlag: { name: string; value: string },
): boolean {
  return experiments[featureFlag.name] === featureFlag.value
}

function resolveLink(
  link: FooterLink,
  showCookiesModal: () => void,
  withLocale: ReturnType<typeof useRouteLocationWithLocale>,
): ResolvedFooterLink {
  const label = translations[link.translationKey]

  if (isConsentLink(link)) {
    return { label, action: showCookiesModal }
  }

  if (isExternalLink(link)) {
    return { label, href: link.href }
  }

  if (isRouteLink(link)) {
    return {
      label,
      to: withLocale(link.route),
      ...(link.prefetch === false && { prefetch: false }),
    }
  }

  return { label }
}

function isLocaleActive(link: FooterLink, locale: Bcp47Locale): boolean {
  if (!link.locales) {
    return true
  }

  return link.locales.includes(locale)
}

function resolveColumn(
  column: FooterColumn,
  experiments: Record<string, string>,
  locale: Bcp47Locale,
  showCookiesModal: () => void,
  withLocale: ReturnType<typeof useRouteLocationWithLocale>,
): ResolvedFooterColumn {
  const links = (column.links ?? [])
    .filter((link) => {
      if ('featureFlag' in link && link.featureFlag) {
        return isFeatureFlagActive(experiments, link.featureFlag)
      }

      return true
    })
    .filter((link) => isLocaleActive(link, locale))
    .map((link) => resolveLink(link, showCookiesModal, withLocale))

  return {
    links,
    ...(column.titleKey && { title: translations[column.titleKey] }),
    ...(column.payment && {
      payment: {
        label: translations[column.payment.translationKey],
        ...(column.payment.href && { href: column.payment.href }),
      },
    }),
    ...(column.socials && { socials: column.socials }),
    ...(column.logos && { logos: column.logos }),
  }
}

export function useFooterConfig(): {
  columns: ComputedRef<ResolvedFooterColumn[]>
} {
  const { market } = useMarketplace()
  const experiments = useExperiments()
  const locale = useI18nLocale()
  const { showCookiesModal } = useDataConsent()
  const withLocale = useRouteLocationWithLocale()

  const columns = computed<ResolvedFooterColumn[]>(() => {
    const config = footerConfigs[market.countryCode]

    if (!config) {
      return []
    }

    return config.columns.map((column) =>
      resolveColumn(column, experiments, locale, showCookiesModal, withLocale),
    )
  })

  return { columns }
}
